import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { publicAnonKey } from '../../../utils/supabase/info';

const API_BASE_URL = 'https://xdqdkwpfihrapeczceqr.supabase.co/functions/v1/make-server-28f1cccf';

type ValidationState = 'validating' | 'success' | 'error';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<ValidationState>('validating');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Support both ?token=xxx and &token=xxx (in case of malformed URLs)
    const urlParams = new URLSearchParams(window.location.search);
    let token = urlParams.get('token');
    
    console.log('🔍 DEBUG - Full URL:', window.location.href);
    console.log('🔍 DEBUG - Pathname:', window.location.pathname);
    console.log('🔍 DEBUG - Search:', window.location.search);
    console.log('🔍 DEBUG - Hash:', window.location.hash);
    console.log('🔍 DEBUG - Token from URLSearchParams:', token);
    console.log('🔍 DEBUG - searchParams from React Router:', searchParams.toString());
    console.log('🔍 DEBUG - token from React Router searchParams:', searchParams.get('token'));
    
    // Prøv også React Router's searchParams
    if (!token) {
      token = searchParams.get('token');
      console.log('🔍 DEBUG - Using React Router token:', token);
    }
    
    // Fallback: Check if token is in the URL path (e.g., /auth&token=xxx)
    if (!token && window.location.pathname.includes('&token=')) {
      const match = window.location.pathname.match(/&token=([^&\/]+)/);
      if (match) {
        token = match[1];
        console.log('🔍 DEBUG - Token from pathname fallback:', token);
      }
    }
    
    // Fallback: Check hash (i.e., /auth#token=xxx)
    if (!token && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      token = hashParams.get('token');
      console.log('🔍 DEBUG - Token from hash:', token);
    }

    if (!token) {
      console.log('❌ DEBUG - Ingen token funnet!');
      setState('error');
      setErrorMessage('Ingen innloggingslenke funnet. Du må bruke lenken fra e-posten du mottok fra Nordic Drift.');
      return;
    }

    console.log('✅ DEBUG - Validerer token:', token);
    validateToken(token);
  }, [searchParams]);

  const validateToken = async (token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/validate-magic-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify({ token })
      });

      if (!response.ok) {
        throw new Error(`API feil: ${response.status}`);
      }

      const result = await response.json();

      if (result.valid) {
        // Lagre kunde-info i localStorage
        localStorage.setItem('customer_id', result.customerId);
        localStorage.setItem('customer_name', result.customerName);
        localStorage.setItem('magic_token', token);

        setState('success');

        // Redirect til opplastingsside etter 1 sekund
        setTimeout(() => {
          navigate('/upload');
        }, 1000);
      } else {
        setState('error');
        setErrorMessage(result.error || 'Ugyldig eller utløpt lenke. Kontakt Nordic Drift for å få en ny lenke.');
      }
    } catch (error) {
      console.error('Token validation error:', error);
      setState('error');
      setErrorMessage('En feil oppstod ved validering av lenken. Vennligst prøv igjen senere.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {state === 'validating' && (
              <div className="bg-blue-600 p-4 rounded-full">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
            {state === 'success' && (
              <div className="bg-green-600 p-4 rounded-full">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
            )}
            {state === 'error' && (
              <div className="bg-red-600 p-4 rounded-full">
                <XCircle className="w-8 h-8 text-white" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl">Nordic Drift</CardTitle>
          <CardDescription className="text-base">
            {state === 'validating' && 'Validerer lenke...'}
            {state === 'success' && 'Lenke validert!'}
            {state === 'error' && 'Ugyldig lenke'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            {state === 'validating' && (
              <p className="text-gray-600">Vennligst vent mens vi validerer din tilgang...</p>
            )}
            {state === 'success' && (
              <p className="text-green-700">Du blir nå videresendt til opplastingssiden...</p>
            )}
            {state === 'error' && (
              <div className="space-y-3">
                <p className="text-red-700">{errorMessage}</p>
                <p className="text-sm text-gray-600">
                  Ta kontakt med Nordic Drift hvis du trenger hjelp.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}